import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, UserCheck, Trophy, Trash2, CheckCircle, XCircle } from 'lucide-react';
import Header from '../../components/Header';
import { useAuth } from '../../context/AuthContext';
import { festivalsAPI, participationsAPI, handleApiError } from '../../utils/api';

const STATUS_LABELS = {
  REGISTERED: { label: 'Inscrit', className: 'bg-blue-100 text-blue-800' },
  CONFIRMED: { label: 'Confirmé', className: 'bg-green-100 text-green-800' },
  CANCELLED: { label: 'Annulé', className: 'bg-gray-200 text-gray-600' },
};

const ManageParticipationsPage = () => {
  const navigate = useNavigate();
  const { festivalId, tournamentId } = useParams();
  const { userRole } = useAuth();

  const [tournament, setTournament] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [resultDrafts, setResultDrafts] = useState({});

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [festivalResponse, participantsResponse] = await Promise.all([
        festivalsAPI.getById(festivalId),
        participationsAPI.getByTournament(tournamentId),
      ]);

      const found = (festivalResponse.data?.subEvents || []).find(
        (t) => String(t.id) === String(tournamentId)
      );
      setTournament(found || null);

      const list = participantsResponse.data || [];
      setParticipants(list);
      setResultDrafts(
        list.reduce((acc, p) => {
          acc[p.id] = {
            finishPosition: p.finishPosition ?? '',
            prizeWon: p.prizeWon ?? '',
            entriesCount: p.entriesCount ?? '',
            buyInPaid: p.buyInPaid ?? '',
          };
          return acc;
        }, {})
      );
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      setError(`${errTitle} : ${details}`);
    } finally {
      setIsLoading(false);
    }
  }, [festivalId, tournamentId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (userRole !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Accès refusé</h2>
            <p className="text-gray-600 mb-6">Vous devez être administrateur pour accéder à cette page.</p>
            <button
              onClick={() => navigate('/')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
            >
              Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleConfirm = async (participation) => {
    setBusyId(participation.id);
    try {
      const response = await participationsAPI.confirm(participation.id);
      setParticipants((prev) => prev.map((p) => (p.id === participation.id ? response.data : p)));
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      alert(`Impossible de confirmer : ${errTitle} - ${details}`);
    } finally {
      setBusyId(null);
    }
  };

  const handleCancel = async (participation) => {
    setBusyId(participation.id);
    try {
      const response = await participationsAPI.cancel(participation.id);
      setParticipants((prev) => prev.map((p) => (p.id === participation.id ? response.data : p)));
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      alert(`Impossible d'annuler : ${errTitle} - ${details}`);
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (participation) => {
    if (!window.confirm(`Retirer "${participation.username}" de ce tournoi ? Cette action est irréversible.`)) {
      return;
    }
    setBusyId(participation.id);
    try {
      await participationsAPI.delete(participation.id);
      setParticipants((prev) => prev.filter((p) => p.id !== participation.id));
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      alert(`Impossible de supprimer : ${errTitle} - ${details}`);
    } finally {
      setBusyId(null);
    }
  };

  const handleDraftChange = (participationId, field, value) => {
    setResultDrafts((prev) => ({
      ...prev,
      [participationId]: { ...prev[participationId], [field]: value },
    }));
  };

  const handleSaveResult = async (participation) => {
    const draft = resultDrafts[participation.id] || {};
    setBusyId(participation.id);
    try {
      const response = await participationsAPI.setResult(participation.id, {
        finishPosition: draft.finishPosition === '' ? null : parseInt(draft.finishPosition, 10),
        prizeWon: draft.prizeWon === '' ? null : parseFloat(draft.prizeWon),
        entriesCount: draft.entriesCount === '' ? null : parseInt(draft.entriesCount, 10),
        buyInPaid: draft.buyInPaid === '' ? null : parseFloat(draft.buyInPaid),
      });
      setParticipants((prev) => prev.map((p) => (p.id === participation.id ? response.data : p)));
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      alert(`Impossible d'enregistrer le résultat : ${errTitle} - ${details}`);
    } finally {
      setBusyId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8 text-center text-gray-500">
          Chargement des participants...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto">
          <button
            onClick={() => navigate(`/manage-festivals/${festivalId}/tournaments`)}
            className="flex items-center text-gray-600 hover:text-blue-600 mb-6 transition"
          >
            <ArrowLeft size={20} className="mr-2" />
            Retour aux tournois
          </button>

          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
              <h1 className="text-3xl font-bold flex items-center">
                <UserCheck className="mr-3" size={32} />
                Participants{tournament ? ` — ${tournament.title}` : ''}
              </h1>
              <p className="mt-2 text-blue-100">
                Gérez les inscriptions et saisissez les résultats une fois le tournoi terminé
              </p>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6">
              {error}
            </div>
          )}

          {participants.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-500">Aucun participant inscrit pour ce tournoi</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-100 text-left text-sm text-gray-600">
                  <tr>
                    <th className="px-4 py-3">Joueur</th>
                    <th className="px-4 py-3">Statut</th>
                    <th className="px-4 py-3">Position</th>
                    <th className="px-4 py-3">Gain (€)</th>
                    <th className="px-4 py-3">Entrées</th>
                    <th className="px-4 py-3">Buy-in payé (€)</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {participants.map((p) => {
                    const statusInfo = STATUS_LABELS[p.status] || STATUS_LABELS.REGISTERED;
                    const draft = resultDrafts[p.id] || { finishPosition: '', prizeWon: '', entriesCount: '', buyInPaid: '' };
                    return (
                      <tr key={p.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-semibold text-gray-800">{p.username}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${statusInfo.className}`}>
                            {statusInfo.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            min="1"
                            value={draft.finishPosition}
                            onChange={(e) => handleDraftChange(p.id, 'finishPosition', e.target.value)}
                            placeholder="Ex: 1"
                            className="w-20 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={draft.prizeWon}
                            onChange={(e) => handleDraftChange(p.id, 'prizeWon', e.target.value)}
                            placeholder="Ex: 500"
                            className="w-28 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            min="1"
                            value={draft.entriesCount}
                            onChange={(e) => handleDraftChange(p.id, 'entriesCount', e.target.value)}
                            placeholder="Ex: 2"
                            className="w-20 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={draft.buyInPaid}
                            onChange={(e) => handleDraftChange(p.id, 'buyInPaid', e.target.value)}
                            placeholder="Ex: 60"
                            className="w-24 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => handleSaveResult(p)}
                              disabled={busyId === p.id}
                              className="bg-purple-600 text-white p-2 rounded-lg hover:bg-purple-700 transition disabled:opacity-50"
                              title="Enregistrer le résultat"
                            >
                              <Trophy size={16} />
                            </button>
                            {p.status !== 'CONFIRMED' && (
                              <button
                                onClick={() => handleConfirm(p)}
                                disabled={busyId === p.id}
                                className="bg-green-600 text-white p-2 rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                                title="Confirmer"
                              >
                                <CheckCircle size={16} />
                              </button>
                            )}
                            {p.status !== 'CANCELLED' && (
                              <button
                                onClick={() => handleCancel(p)}
                                disabled={busyId === p.id}
                                className="bg-orange-500 text-white p-2 rounded-lg hover:bg-orange-600 transition disabled:opacity-50"
                                title="Annuler"
                              >
                                <XCircle size={16} />
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(p)}
                              disabled={busyId === p.id}
                              className="bg-red-600 text-white p-2 rounded-lg hover:bg-red-700 transition disabled:opacity-50"
                              title="Retirer"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageParticipationsPage;
